/**
 * RewardScene - Choose an upgrade after winning a battle.
 * Offers are context-filtered and rarity-weighted by the tier of the fight won.
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { getUpgradeOffers, Upgrade } from '../game/upgrades/UpgradeLibrary';
import { SCENES, GAME_CONFIG } from '../constants/GameConstants';
import { FloorData } from '../types';
import { AudioManager } from '../game/AudioManager';
import { T, INK, PALETTE, RARITY, RARITY_HEX, makeButton, makePanel, paintBackground, fadeInScene } from '../constants/Theme';

export class RewardScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;
  private floorData!: FloorData;
  private choiceMade = false;

  constructor() {
    super({ key: SCENES.REWARD });
  }

  init(data: { floorData?: FloorData } = {}): void {
    this.gameStateManager = GameStateManager.getInstance();
    this.floorData = data.floorData || this.gameStateManager.getCurrentFloorData();
    this.choiceMade = false;
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;

    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);

    const tier = this.floorData.type === 'boss' ? 'boss' : this.floorData.type === 'elite' ? 'elite' : 'normal';
    const titleText = tier === 'normal' ? 'CHOOSE A REWARD' : tier === 'elite' ? 'ELITE SPOILS' : 'BOSS TREASURE';

    const title = this.add.text(cx, 70, titleText, T.title({ color: INK.gold }));
    title.setOrigin(0.5);
    title.setLetterSpacing(2);

    // Gold pill
    const gold = this.gameStateManager.getGameState().gold;
    makePanel(this, cx, 130, 220, 50, { fill: PALETTE.panel, radius: 25 });
    const goldText = this.add.text(cx, 130, `◆ ${gold} gold`, T.label({ color: INK.gold }));
    goldText.setOrigin(0.5);

    // Roll offers with run context
    const count = Math.max(1, this.floorData.rewards.upgradeOptions);
    const offers = getUpgradeOffers(count, this.gameStateManager.getOfferContext(tier));

    const cardH = 138;
    const gap = 18;
    const total = offers.length * cardH + (offers.length - 1) * gap;
    let y = 185 + cardH / 2;
    const startY = Math.max(185 + cardH / 2, (HEIGHT - 170 - total) / 2 + cardH / 2);
    y = startY;

    for (const upgrade of offers) {
      this.createUpgradeCard(cx, y, upgrade, cardH);
      y += cardH + gap;
    }

    if (offers.length === 0) {
      const empty = this.add.text(cx, 400, 'Your collection is complete!\nTake some gold instead.',
        T.heading({ align: 'center', color: INK.secondary, lineSpacing: 8 }));
      empty.setOrigin(0.5);
    }

    this.createSkipButton(cx, HEIGHT - 90, offers.length === 0 ? 30 : 10);
  }

  private createUpgradeCard(x: number, y: number, upgrade: Upgrade, h: number): void {
    const w = 640;
    const rarityHex = RARITY_HEX[upgrade.rarity];
    const rarityStr = RARITY[upgrade.rarity];

    const panel = makePanel(this, x, y, w, h, { fill: PALETTE.panel, stroke: rarityHex, strokeWidth: 3, radius: 18 });

    // Rarity accent strip on the left
    const strip = this.add.graphics();
    strip.fillStyle(rarityHex, 1);
    strip.fillRoundedRect(x - w / 2, y - h / 2, 10, h, { tl: 18, bl: 18, tr: 0, br: 0 });

    const badge = this.add.text(x - w / 2 + 28, y - h / 2 + 16, upgrade.rarity.toUpperCase(),
      T.micro({ color: rarityStr }));
    badge.setLetterSpacing(2);

    const name = this.add.text(x, y - 26, upgrade.name, T.heading({ color: rarityStr }));
    name.setOrigin(0.5);

    const desc = this.add.text(x, y + 26, upgrade.description, T.body({
      align: 'center', color: INK.secondary, wordWrap: { width: w - 70 }, lineSpacing: 2,
    }));
    desc.setOrigin(0.5);

    const hit = this.add.rectangle(x, y, w, h, 0x000000, 0.001);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => panel.setAlpha(0.85));
    hit.on('pointerout', () => panel.setAlpha(1));
    hit.on('pointerdown', () => {
      if (this.choiceMade) return;
      this.choiceMade = true;
      this.gameStateManager.applyUpgrade(upgrade);
      AudioManager.getInstance().play('upgrade');
      this.showAcquiredFeedback(upgrade);
      this.time.delayedCall(1000, () => this.proceedToMap());
    });
  }

  private showAcquiredFeedback(upgrade: Upgrade): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const overlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x0a0f1c, 0.75).setOrigin(0);
    overlay.setDepth(300);

    const text = this.add.text(WIDTH / 2, HEIGHT / 2, `Acquired:\n${upgrade.name}`, T.heading({
      align: 'center', color: RARITY[upgrade.rarity], wordWrap: { width: WIDTH - 80 }, lineSpacing: 8,
    }));
    text.setOrigin(0.5);
    text.setDepth(301);
    text.setAlpha(0);
    this.tweens.add({ targets: text, alpha: 1, scale: { from: 0.9, to: 1 }, duration: 250, ease: 'Back.out' });
  }

  private createSkipButton(x: number, y: number, goldInstead: number): void {
    makeButton(this, x, y, `SKIP  (+${goldInstead} gold)`, {
      width: 340, fill: PALETTE.panelRaised, fontSize: 24,
      onClick: () => {
        if (this.choiceMade) return;
        this.choiceMade = true;
        this.gameStateManager.addGold(goldInstead);
        this.gameStateManager.saveGame();
        this.proceedToMap();
      },
    });
  }

  private proceedToMap(): void {
    this.scene.start(SCENES.MAP);
  }
}
